import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import DesignedModalShell from '../components/designed/DesignedModalShell';
import { DIALOG_VARIANTS, type DialogVariant } from '../components/designed/dialogVariants';

export type ConfirmDialogOptions = {
  title: string;
  message: string;
  bullets?: string[];
  hint?: string;
  variant?: DialogVariant;
  attentionLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

export type AlertDialogOptions = {
  title?: string;
  message: string;
  variant?: DialogVariant;
  attentionLabel?: string;
  okLabel?: string;
};

type AppDialogContextValue = {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  alert: (options: AlertDialogOptions | string) => Promise<void>;
};

const AppDialogContext = createContext<AppDialogContextValue | null>(null);

type ConfirmState = ConfirmDialogOptions & { resolve: (v: boolean) => void };
type AlertState = Required<Pick<AlertDialogOptions, 'message'>> &
  Omit<AlertDialogOptions, 'message'> & { resolve: () => void };

export const AppDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [alertState, setAlertState] = useState<AlertState | null>(null);
  const busyRef = useRef(false);

  const confirm = useCallback((options: ConfirmDialogOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...options, resolve });
    });
  }, []);

  const alert = useCallback((options: AlertDialogOptions | string) => {
    const normalized: AlertDialogOptions =
      typeof options === 'string' ? { message: options } : options;
    return new Promise<void>((resolve) => {
      setAlertState({
        message: normalized.message,
        title: normalized.title,
        variant: normalized.variant,
        attentionLabel: normalized.attentionLabel,
        okLabel: normalized.okLabel,
        resolve,
      });
    });
  }, []);

  const closeConfirm = (result: boolean) => {
    if (!confirmState || busyRef.current) return;
    confirmState.resolve(result);
    setConfirmState(null);
  };

  const closeAlert = () => {
    if (!alertState) return;
    alertState.resolve();
    setAlertState(null);
  };

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  const confirmVariant = confirmState?.variant ?? 'warning';
  const confirmStyle = DIALOG_VARIANTS[confirmVariant];

  return (
    <AppDialogContext.Provider value={value}>
      {children}

      {confirmState && (
        <DesignedModalShell
          open
          variant={confirmVariant}
          attentionLabel={confirmState.attentionLabel}
          title={confirmState.title}
          onClose={() => closeConfirm(false)}
          closeDisabled={busyRef.current}
          role="alertdialog"
          zIndex={10070}
          maxWidth={520}
          footer={
            <div className="modal-actions" style={{ marginTop: 0, gap: '0.75rem' }}>
              <button
                type="button"
                className="btn-secondary"
                style={{ flex: 1 }}
                onClick={() => closeConfirm(false)}
                disabled={busyRef.current}
              >
                {confirmState.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className="btn-primary"
                style={{
                  flex: 1,
                  background: confirmStyle.confirmBg,
                  border: 'none',
                  boxShadow: confirmStyle.confirmShadow,
                }}
                onClick={() => closeConfirm(true)}
                disabled={busyRef.current}
              >
                {confirmState.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {confirmState.message}
          </p>
          {confirmState.bullets && confirmState.bullets.length > 0 && (
            <ul style={{ margin: '1rem 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {confirmState.bullets.map((b) => (
                <li key={b} style={{ fontSize: '0.85rem', color: '#334155', paddingLeft: '1rem', borderLeft: '3px solid #e2e8f0' }}>
                  {b}
                </li>
              ))}
            </ul>
          )}
          {confirmState.hint && (
            <p
              style={{
                margin: '1rem 0 0',
                padding: '0.75rem 0.9rem',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '10px',
                fontSize: '0.8rem',
                color: '#92400e',
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              {confirmState.hint}
            </p>
          )}
        </DesignedModalShell>
      )}

      {alertState && (
        <DesignedModalShell
          open
          variant={alertState.variant ?? 'info'}
          attentionLabel={alertState.attentionLabel}
          title={alertState.title ?? 'Notice'}
          onClose={closeAlert}
          role="alertdialog"
          zIndex={10075}
          maxWidth={440}
          footer={
            <div className="modal-actions" style={{ marginTop: 0 }}>
              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%' }}
                onClick={closeAlert}
                data-e2e="dialog-ok"
              >
                {alertState.okLabel ?? 'OK'}
              </button>
            </div>
          }
        >
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {alertState.message}
          </p>
        </DesignedModalShell>
      )}
    </AppDialogContext.Provider>
  );
};

export function useAppDialog(): AppDialogContextValue {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error('useAppDialog must be used within AppDialogProvider');
  }
  return ctx;
}

/** Shorthand helpers for migrating `alert('...')` / `window.confirm`. */
export function useDesignedDialog() {
  const { alert, confirm } = useAppDialog();
  // Stable identity so consumers can safely put `dlg` in useCallback/useEffect deps.
  return useMemo(
    () => ({
      alert: (message: string, opts?: Omit<AlertDialogOptions, 'message'>) =>
        alert({ message, ...opts }),
      alertError: (message: string, title = 'Something went wrong') =>
        alert({ message, title, variant: 'danger' }),
      alertSuccess: (message: string, title = 'Done') =>
        alert({ message, title, variant: 'success' }),
      confirm: (
        message: string,
        opts?: Omit<ConfirmDialogOptions, 'message'> & { title?: string }
      ) =>
        confirm({
          title: opts?.title ?? 'Confirm',
          message,
          variant: opts?.variant ?? 'warning',
          confirmLabel: opts?.confirmLabel,
          cancelLabel: opts?.cancelLabel,
          bullets: opts?.bullets,
          hint: opts?.hint,
          attentionLabel: opts?.attentionLabel,
        }),
      confirmDanger: (message: string, title: string, confirmLabel = 'Yes, continue') =>
        confirm({
          title,
          message,
          variant: 'danger',
          confirmLabel,
          attentionLabel: 'This cannot be undone',
        }),
      confirmRaw: confirm,
      alertRaw: alert,
    }),
    [alert, confirm]
  );
}